import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

/**
 * Custom hook for drag and drop functionality with localStorage persistence
 * @param {Array} items - The items to be reordered
 * @param {string} storageKey - localStorage key to persist the order
 * @returns {Object} - Drag and drop handlers and state
 */
export const useDragAndDrop = (items, storageKey) => {
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedOverIndex, setDraggedOverIndex] = useState(null);
    const [orderedItems, setOrderedItems] = useState([]);
    const isInitializedRef = useRef(false);
    const itemsIdsRef = useRef('');
    const itemsRef = useRef(items);

    // Update items ref whenever items change
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    // Create a stable string representation of items IDs for comparison
    const currentItemsIds = useMemo(() => {
        if (!items || items.length === 0) return '';
        return items.map(item => item.id).join(',');
    }, [items]);

    // Load order from localStorage on mount or when items IDs change
    useEffect(() => {
        const currentItems = itemsRef.current;
        
        // Only update if the items IDs have actually changed
        if (currentItemsIds === itemsIdsRef.current && isInitializedRef.current) {
            return;
        }

        itemsIdsRef.current = currentItemsIds;

        if (!currentItems || currentItems.length === 0) {
            setOrderedItems([]);
            isInitializedRef.current = true;
            return;
        }

        try {
            const savedOrder = localStorage.getItem(storageKey);
            if (savedOrder) {
                const orderArray = JSON.parse(savedOrder);
                // Create a map of items by id for quick lookup
                const itemsMap = new Map(currentItems.map(item => [item.id, item]));
                
                // Reorder items based on saved order, filtering out any that no longer exist
                const reordered = orderArray
                    .map(id => itemsMap.get(id))
                    .filter(Boolean);
                
                // Add any new items that weren't in the saved order
                const existingIds = new Set(reordered.map(item => item.id));
                const newItems = currentItems.filter(item => !existingIds.has(item.id));
                
                const finalOrder = [...reordered, ...newItems];
                setOrderedItems(finalOrder);
            } else {
                setOrderedItems(currentItems);
            }
        } catch (error) {
            console.error('Error loading order from localStorage:', error);
            setOrderedItems(currentItems);
        }
        
        isInitializedRef.current = true;
    }, [currentItemsIds, storageKey]);

    // Save order to localStorage whenever it changes (but not on initial load)
    useEffect(() => {
        if (!isInitializedRef.current || orderedItems.length === 0) {
            return;
        }

        try {
            const orderArray = orderedItems.map(item => item.id);
            const currentSaved = localStorage.getItem(storageKey);
            const savedArray = currentSaved ? JSON.parse(currentSaved) : [];
            
            // Only save if the order has actually changed
            if (JSON.stringify(orderArray) !== JSON.stringify(savedArray)) {
                localStorage.setItem(storageKey, JSON.stringify(orderArray));
            }
        } catch (error) {
            console.error('Error saving order to localStorage:', error);
        }
    }, [orderedItems, storageKey]);

    const handleDragStart = useCallback((e, index) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', index.toString());
        setDraggedItem(index);
        
        // Find the parent card container to use as drag image
        const cardElement = e.target.closest('[data-draggable-card]');
        if (cardElement) {
            // Create a custom drag image
            const dragImage = cardElement.cloneNode(true);
            dragImage.style.opacity = '0.8';
            dragImage.style.transform = 'rotate(2deg)';
            dragImage.style.position = 'absolute';
            dragImage.style.top = '-9999px';
            document.body.appendChild(dragImage);
            e.dataTransfer.setDragImage(dragImage, 0, 0);
            setTimeout(() => {
                if (document.body.contains(dragImage)) {
                    document.body.removeChild(dragImage);
                }
            }, 0);
        }
    }, []);

    const handleDragOver = useCallback((e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedItem === null || draggedItem === index) return;
        
        setDraggedOverIndex(index);
    }, [draggedItem]);

    const handleDragLeave = useCallback(() => {
        setDraggedOverIndex(null);
    }, []);

    const handleDrop = useCallback((e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (draggedItem === null || draggedItem === dropIndex) {
            setDraggedItem(null);
            setDraggedOverIndex(null);
            return;
        }

        const newOrderedItems = [...orderedItems];
        const draggedItemData = newOrderedItems[draggedItem];
        
        // Remove dragged item from its original position
        newOrderedItems.splice(draggedItem, 1);
        
        // Insert dragged item at new position
        newOrderedItems.splice(dropIndex, 0, draggedItemData);
        
        setOrderedItems(newOrderedItems);
        setDraggedItem(null);
        setDraggedOverIndex(null);
    }, [draggedItem, orderedItems]);

    const handleDragEnd = useCallback((e) => {
        // Reset any inline styles that might have been set
        const cardElement = e.target.closest('[data-draggable-card]');
        if (cardElement) {
            cardElement.style.opacity = '';
            cardElement.style.transform = '';
        }
        // Also reset the drag handle if needed
        const dragHandle = e.target.closest('.drag-handle');
        if (dragHandle) {
            dragHandle.style.opacity = '';
        }
        setDraggedItem(null);
        setDraggedOverIndex(null);
    }, []);

    return {
        orderedItems,
        draggedItem,
        draggedOverIndex,
        handleDragStart,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragEnd,
    };
};

